import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Eye, Sparkles } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Observing CuSO₄ Solution',
    content: 'Copper(II) sulfate solution displays the characteristic blue color of hydrated Cu²⁺ ions. In aqueous solution, each Cu²⁺ ion is surrounded by 6 water molecules in an octahedral coordination: [Cu(H₂O)₆]²⁺',
    whatHappens: 'You observe the bright blue CuSO₄ solution. The color arises because the copper-water complex absorbs red and orange light, transmitting blue light to your eyes.',
    observe: [
      'Brilliant blue color of the solution',
      'Solution should be clear and transparent',
      'No undissolved particles visible',
      'Color is uniform throughout'
    ]
  },
  2: {
    title: 'Recording Observations',
    content: 'Careful documentation is essential in chemistry. Recording observations helps you track the success of each reaction step and identify any problems. The copper cycle demonstrates conservation of matter - all the copper atoms are still present.',
    whatHappens: 'You document the appearance of the CuSO₄ solution in preparation for the final reduction step where copper metal will be recovered.',
    observe: [
      'Note the exact shade of blue',
      'Check for any precipitate or cloudiness',
      'Estimate the volume of solution',
      'Record any crystals forming on the beaker walls'
    ]
  }
};

export default function Part5Sulfate() {
  const [observed, setObserved] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [observed, recorded].filter(Boolean).length;

  const handleObserve = useCallback(() => {
    if (!observed) {
      setObserved(true);
      setCurrentStep(2);
    }
  }, [observed]);

  const handleRecord = useCallback(() => {
    if (observed && !recorded) {
      setRecorded(true);
    }
  }, [observed, recorded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part V: CuSO₄ Observation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Observe Solution', hint: 'Note the bright blue color', done: observed },
            { id: 2, title: 'Record Observations', hint: 'Document crystal formation', done: recorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Information panel */}
        <div className="info-panel">
          <h4>Copper(II) Sulfate</h4>
          <div className="formula">CuSO₄ · 5H₂O</div>
          <p>The bright blue color is due to the Cu²⁺ ion coordinated with water molecules.</p>
        </div>
      </div>

      <LabBench
        experimentName="CuSO₄ Crystal Observation"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['CuSO₄ solution']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="observation-container">
            {/* Large display beaker */}
            <div
              className={`observation-beaker ${currentStep === 1 && !observed ? 'clickable' : ''}`}
              onClick={handleObserve}
            >
              <svg viewBox="0 0 200 260" width={220} height={280}>
                <defs>
                  <linearGradient id={`beaker-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id={`cuso4-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={copperCycleColors.cuSO4Solution} stopOpacity="0.85" />
                    <stop offset="100%" stopColor={copperCycleColors.cuSO4Solution} stopOpacity="0.95" />
                  </linearGradient>
                  <radialGradient id={`highlight-${id}`} cx="30%" cy="30%" r="70%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                  </radialGradient>
                </defs>

                {/* Glow effect when observing */}
                {observed && (
                  <ellipse cx="100" cy="130" rx="80" ry="100" fill="none" stroke={copperCycleColors.cuSO4Solution} strokeWidth="3" opacity="0.4">
                    <animate attributeName="opacity" values="0.4;0.6;0.4" dur="2s" repeatCount="indefinite" />
                  </ellipse>
                )}

                {/* Beaker */}
                <rect x="30" y="30" width="140" height="180" fill={`url(#beaker-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="3" />

                {/* CuSO₄ Solution */}
                <rect x="34" y="60" width="132" height="146" fill={`url(#cuso4-${id})`} />

                {/* Crystal hints on bottom */}
                <g opacity="0.6">
                  <polygon points="50,200 60,190 55,205" fill="#4169E1" />
                  <polygon points="80,202 90,192 85,207" fill="#0066CC" />
                  <polygon points="120,198 130,188 125,203" fill="#4169E1" />
                  <polygon points="145,200 155,190 150,205" fill="#0066CC" />
                </g>

                {/* Surface meniscus */}
                <ellipse cx="100" cy="60" rx="64" ry="4" fill={copperCycleColors.cuSO4Solution} opacity="0.9" />
                <ellipse cx="85" cy="60" rx="30" ry="2" fill="rgba(255,255,255,0.3)" />

                {/* Light sparkles */}
                {observed && (
                  <g>
                    <circle cx="60" cy="100" r="2" fill="#FFFFFF" opacity="0.8">
                      <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="140" cy="120" r="1.5" fill="#FFFFFF" opacity="0.6">
                      <animate attributeName="opacity" values="0.6;0.1;0.6" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="90" cy="160" r="2" fill="#FFFFFF" opacity="0.7">
                      <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.8s" repeatCount="indefinite" />
                    </circle>
                  </g>
                )}

                {/* Highlight reflection */}
                <rect x="38" y="65" width="8" height="100" fill="rgba(255,255,255,0.3)" rx="2" />

                {/* Boiling chips */}
                <circle cx="65" cy="198" r="3" fill={copperCycleColors.boilingChips} />
                <circle cx="130" cy="196" r="3" fill={copperCycleColors.boilingChips} />

                <text x="100" y="230" fontSize="12" fill="#E0E0E0" textAnchor="middle">CuSO₄ Solution</text>
                <text x="100" y="245" fontSize="10" fill={copperCycleColors.cuSO4Solution} textAnchor="middle">Bright Blue</text>
              </svg>
            </div>

            {/* Observation notes */}
            {observed && (
              <div className="observation-notes">
                <h4><Eye size={16} /> Observations</h4>
                <ul>
                  <li>
                    <span className="color-dot" style={{ background: copperCycleColors.cuSO4Solution }}></span>
                    Bright blue color (Cu²⁺ ions)
                  </li>
                  <li>
                    <Sparkles size={14} />
                    Clear, transparent solution
                  </li>
                  <li>
                    <Sparkles size={14} />
                    Small crystals may form on cooling
                  </li>
                </ul>
                <button
                  className={`record-btn ${recorded ? 'recorded' : ''}`}
                  onClick={handleRecord}
                  disabled={recorded}
                >
                  {recorded ? <Check size={16} /> : null}
                  {recorded ? 'Recorded' : 'Record Observations'}
                </button>
              </div>
            )}
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !observed && (
              <div className="status-message info">
                <Eye size={20} />
                Click the beaker to observe the CuSO₄ solution
              </div>
            )}
            {observed && !recorded && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Record your observations before proceeding
              </div>
            )}
            {recorded && (
              <div className="status-message success">
                <Check size={20} />
                CuSO₄ solution ready for the next reaction!
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Why Is It Blue?</h4>
            <p>
              The beautiful blue color of copper(II) sulfate solution comes from the hydrated Cu²⁺ ion.
              In solution, each Cu²⁺ is surrounded by 6 water molecules in an octahedral arrangement.
              This complex absorbs red/orange light and transmits blue light.
            </p>
            <div className="color-spectrum">
              <span className="absorbed">Absorbed</span>
              <div className="spectrum">
                <div className="band red"></div>
                <div className="band orange"></div>
                <div className="band yellow"></div>
                <div className="band green"></div>
                <div className="band blue transmitted"></div>
                <div className="band violet"></div>
              </div>
              <span className="transmitted-label">Transmitted</span>
            </div>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .observation-container {
            display: flex;
            gap: 40px;
            align-items: center;
            justify-content: center;
          }

          .observation-beaker {
            padding: 10px;
            border-radius: 12px;
            transition: all 0.3s ease;
          }

          .observation-beaker.clickable {
            cursor: pointer;
          }

          .observation-beaker.clickable:hover {
            background: rgba(0, 102, 204, 0.1);
            transform: scale(1.02);
          }

          .observation-notes {
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
            min-width: 250px;
            border-left: 4px solid ${copperCycleColors.cuSO4Solution};
          }

          .observation-notes h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 16px 0;
            color: var(--text-primary);
          }

          .observation-notes ul {
            list-style: none;
            padding: 0;
            margin: 0 0 16px 0;
          }

          .observation-notes li {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 0;
            border-bottom: 1px solid var(--border);
            font-size: 13px;
            color: var(--text-secondary);
          }

          .color-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }

          .record-btn {
            width: 100%;
            padding: 10px 16px;
            background: ${copperCycleColors.cuSO4Solution};
            color: white;
            border: none;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s;
          }

          .record-btn:hover:not(:disabled) {
            background: #0077DD;
          }

          .record-btn.recorded {
            background: var(--secondary);
          }

          .record-btn:disabled {
            cursor: default;
          }

          .info-panel {
            margin-top: 16px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 8px;
          }

          .info-panel h4 {
            margin: 0 0 4px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .info-panel .formula {
            font-family: 'Courier New', monospace;
            color: ${copperCycleColors.cuSO4Solution};
            font-weight: bold;
            margin-bottom: 8px;
          }

          .info-panel p {
            margin: 0;
            font-size: 11px;
            color: var(--text-secondary);
            line-height: 1.4;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(0, 102, 204, 0.2);
            color: ${copperCycleColors.cuSO4Solution};
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid ${copperCycleColors.cuSO4Solution};
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: ${copperCycleColors.cuSO4Solution};
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .color-spectrum {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 10px;
          }

          .spectrum {
            display: flex;
            height: 20px;
            border-radius: 3px;
            overflow: hidden;
            flex: 1;
          }

          .band {
            flex: 1;
            opacity: 0.3;
          }

          .band.red { background: #FF0000; }
          .band.orange { background: #FF7F00; }
          .band.yellow { background: #FFFF00; }
          .band.green { background: #00FF00; }
          .band.blue { background: #0000FF; }
          .band.violet { background: #8B00FF; }

          .band.transmitted {
            opacity: 1;
            box-shadow: 0 0 8px #0066CC;
          }

          .absorbed { color: #888; }
          .transmitted-label { color: ${copperCycleColors.cuSO4Solution}; }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
